<template>
  <div class="flex h-full grow flex-col services-guide-layout">
    <main class="flex flex-1 flex-col md:flex-row">
      <!-- Left sidebar -->
      <aside class="w-full md:w-72 bg-white dark:bg-slate-800 border-r border-slate-200 dark:border-slate-700 p-6 space-y-6 team-sidebar">
        <h2 class="text-slate-900 dark:text-slate-100 text-lg font-semibold leading-tight tracking-[-0.015em] px-1">Equipos</h2>
        <div class="flex flex-col gap-1">
          <button 
            v-for="category in categories" 
            :key="category.id"
            @click="activeCategory = category.id"
            :class="[
              'team-button flex items-center gap-3 px-3 py-2.5 rounded-lg transition-colors',
              activeCategory === category.id 
                ? 'active bg-orange-50 dark:bg-orange-900/50 text-orange-600 dark:text-orange-400 border border-orange-200 dark:border-orange-700' 
                : 'text-slate-700 dark:text-slate-300 hover:bg-orange-50 dark:hover:bg-orange-900/30 hover:text-orange-600 dark:hover:text-orange-400'
            ]"
          >
            <span class="material-icons-outlined">{{ category.icon }}</span>
            <span class="text-sm font-medium leading-normal">{{ category.name }}</span>
          </button>
        </div>
      </aside>

      <!-- Main content -->
      <section class="flex-1 p-6 md:p-10">
        <div class="mb-6">
          <h1 class="text-slate-900 dark:text-slate-100 text-3xl font-bold leading-tight tracking-tight">Guía de Servicios</h1>
          <p class="text-slate-600 dark:text-slate-400 text-base font-normal leading-normal mt-1">
            Catálogo de servicios con tiempos estimados - {{ categories.find(cat => cat.id === activeCategory)?.name }}
          </p>
        </div>

        <!-- Search box -->
        <div class="mb-6">
          <div class="relative">
            <span class="absolute inset-y-0 left-0 flex items-center pl-3 text-slate-400 dark:text-slate-500">
              <span class="material-icons-outlined text-xl">search</span>
            </span>
            <input 
              type="text" 
              v-model="searchQuery" 
              placeholder="Buscar servicios..." 
              class="w-full py-3 pl-10 pr-4 border border-slate-200 dark:border-slate-600 dark:bg-slate-700 dark:text-slate-100 dark:placeholder-slate-400 rounded-lg focus:outline-none focus:ring-2 focus:ring-orange-500 focus:border-orange-500 dark:focus:ring-orange-400 dark:focus:border-orange-400"
            />
            <button 
              v-if="searchQuery" 
              @click="searchQuery = ''" 
              class="absolute inset-y-0 right-0 flex items-center pr-3 text-slate-400 dark:text-slate-500 hover:text-slate-600 dark:hover:text-slate-300"
            >
              <span class="material-icons-outlined">close</span>
            </button>
          </div>
        </div>

        <!-- Service Categories -->
        <div class="space-y-4">
          <!-- Display groups for the active team -->
          <details 
            v-for="group in filteredGroups" 
            :key="group.group"
            class="service-group-details flex flex-col rounded-xl border border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-800 shadow-sm group"
          >
            <summary class="group-header flex cursor-pointer items-center justify-between gap-4 p-4 hover:bg-slate-50 dark:hover:bg-slate-700 rounded-t-xl">
              <div class="flex items-center gap-3">
                <span class="material-icons-outlined text-orange-600 dark:text-orange-400">folder</span>
                <p class="group-title text-slate-800 dark:text-slate-200 text-base font-semibold leading-normal">{{ group.group }}</p>
                <span class="group-count text-sm text-orange-600 dark:text-orange-400">({{ group.items.filter(item => item.name).length }} servicios)</span>
              </div>
              <div class="chevron-icon text-slate-500 dark:text-slate-400 transition-transform duration-200">
                <svg fill="currentColor" height="20px" viewBox="0 0 256 256" width="20px" xmlns="http://www.w3.org/2000/svg">
                  <path d="M213.66,101.66l-80,80a8,8,0,0,1-11.32,0l-80-80A8,8,0,0,1,53.66,90.34L128,164.69l74.34-74.35a8,8,0,0,1,11.32,11.32Z"></path>
                </svg>
              </div>
            </summary>
            <div class="p-4 border-t border-slate-200 dark:border-slate-700 space-y-3">
              
              <!-- No results message when filtered -->
              <div v-if="filteredItems(group).length === 0 && searchQuery" class="py-4 text-center">
                <p class="text-slate-500 dark:text-slate-400 text-sm">No se encontraron servicios que coincidan con "{{ searchQuery }}"</p>
              </div>
              
              <!-- Services list for this group -->
              <div class="services-group-container">
                <div 
                  v-for="(service, index) in filteredItems(group)" 
                  :key="index"
                  class="service-item p-4 bg-slate-50 dark:bg-slate-700 rounded-lg border border-slate-200 dark:border-slate-600 space-y-2"
                  :class="{'highlighted border-orange-300 dark:border-orange-600 bg-orange-50 dark:bg-orange-900/50': highlightMatch(service)}"
                >
                  <div class="flex justify-between items-start">
                    <div class="flex-1">
                      <h4 class="font-semibold text-slate-800 dark:text-slate-200 text-lg">{{ service.name }}</h4>
                      <div class="flex items-center gap-2 mt-1 mb-2">
                        <span class="service-hours-badge">{{ service.hours }} horas</span>
                      </div>
                      <p class="service-description text-slate-600 dark:text-slate-400">{{ service.description }}</p>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </details>
          
          <!-- No results message when no groups match -->
          <div v-if="filteredGroups.length === 0" class="empty-state py-8 text-center">
            <div class="text-slate-400 dark:text-slate-500 mb-2">
              <span class="material-icons-outlined text-4xl">search_off</span>
            </div>
            <p class="text-slate-500 dark:text-slate-400">No se encontraron servicios que coincidan con "{{ searchQuery }}"</p>
          </div>
        </div>
      </section>
    </main>
    <footer-section />
  </div>
</template>

<script>
import FooterSection from '../components/ui/Footer.vue';
import { adminAPI } from '@/services/api';

export default {
  name: 'ServicesGuide',
  components: {
    FooterSection
  },
  data() {
    return {
      activeCategory: null,
      searchQuery: '',
      categories: [],
      teams: [],
      groups: [],
      items: [],
      loading: true,
      loadingItems: false
    }
  },
  async mounted() {
    await this.loadTeams();
  },
  watch: {
    async activeCategory(newVal) {
      if (newVal) {
        await this.loadGroupsAndItems(newVal);
      }
    }
  },
  computed: {
    /**
     * Get the currently active team data
     */
    activeTeam() {
      if (!this.activeCategory || this.teams.length === 0) return null;
      
      const team = this.teams.find(t => t.id === this.activeCategory);
      if (!team) return null;
      
      // Build structure similar to old services format
      return {
        team: team.name,
        groups: this.groupsWithItems
      };
    },
    
    /**
     * Build groups with their items
     */
    groupsWithItems() {
      if (!this.activeCategory) return [];
      
      const teamGroups = this.groups.filter(g => g.team_id === this.activeCategory);
      
      return teamGroups.map(group => ({
        group: group.name,
        items: this.items.filter(item => item.group_id === group.id)
      }));
    },
    
    /**
     * Get groups for the active team, filtered by search query
     */
    filteredGroups() {
      if (!this.activeTeam) return [];
      
      if (!this.searchQuery.trim()) {
        return this.activeTeam.groups;
      }
      
      // Filter groups that have at least one matching service
      return this.activeTeam.groups.filter(group => {
        return this.filteredItems(group).length > 0;
      });
    }
  },
  methods: {
    async loadTeams() {
      this.loading = true;
      try {
        const data = await adminAPI.getTeams();
        if (data.success) {
          this.teams = data.teams || [];
          // Transform teams to categories format
          this.categories = this.teams.map(team => ({
            id: team.id,
            name: team.name,
            icon: team.icon || 'group'
          }));
          // Select first team by default
          if (this.categories.length > 0) {
            this.activeCategory = this.categories[0].id;
            await this.loadGroupsAndItems(this.activeCategory);
          }
        }
      } catch (error) {
        console.error('Error loading teams:', error);
      } finally {
        this.loading = false;
      }
    },
    
    async loadGroupsAndItems(teamId) {
      this.loadingItems = true;
      try {
        // Load groups for the team
        const groupsData = await adminAPI.getGroups({ team_id: teamId });
        if (groupsData.success) {
          this.groups = groupsData.groups || [];
        }
        
        // Load items for the team
        const itemsData = await adminAPI.getItems({ team_id: teamId, limit: 500 });
        if (itemsData.success) {
          this.items = itemsData.items || [];
        }
      } catch (error) {
        console.error('Error loading groups and items:', error);
      } finally {
        this.loadingItems = false;
      }
    },
    
    /**
     * Filter items in a group based on search query
     */
    filteredItems(group) {
      // Only return items that have a name (filter out separators)
      const validItems = group.items.filter(item => item.name);
      
      if (!this.searchQuery.trim()) {
        return validItems;
      }
      
      const query = this.searchQuery.toLowerCase();
      return validItems.filter(service => 
        service.name.toLowerCase().includes(query) || 
        (service.description && service.description.toLowerCase().includes(query))
      );
    },
    
    /**
     * Check if service should be highlighted (exact match)
     */
    highlightMatch(service) {
      if (!this.searchQuery.trim()) {
        return false;
      }
      
      const query = this.searchQuery.toLowerCase();
      return service.name.toLowerCase() === query;
    }
  }
}
</script>
